import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Plus, Pencil, Trash2, Eye, Globe } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Skeleton,
  Checkbox,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, FlowDiagram, FlowRow, type FlowNode, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { toneColor } from '@/pages/services/shared';
import {
  useServiceWafStrategies,
  useServiceWafStrategiesMapping,
  useServiceWafCustomStrategies,
  useSetServiceWafMonitor,
  useApplyServiceWafPolicy,
  useDeleteServiceCustomPolicy,
  waapAsArray,
  type WafStrategyRow,
  type WafStrategyMappingRow,
  type WafCustomStrategyRow,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { WAF_POLICIES, type WafPolicy } from '@/pages/services/waap/waf-policies';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './GeneralProtectionTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * The Custom WAF Protection Policy block. Parked while the vendor edit/delete
 * contracts were unknown; both are now captured — edit is a PUT of the whole
 * config, delete is by policy id — so the block is live again.
 */
const SHOW_CUSTOM_WAF_POLICY = true;

/** Render a domain list as comma-separated muted text (or an em dash). */
function DomainList({ domains }: { domains: string[] }) {
  if (!domains.length) return muted('—');
  return (
    <div className={styles.domainList}>
      {domains.map((d) => (
        <StatusPill key={d} tone="neutral">
          {d}
        </StatusPill>
      ))}
    </div>
  );
}

/** Coerce a possibly-string strategy id to a number for joining. */
const asId = (v: number | string | undefined): number | undefined => {
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** A protected site, derived from a waf_strategies row. */
interface SiteInfo {
  siteId: number;
  domain: string;
  monitorOnly: boolean;
  strategyType?: number;
}

/**
 * AI-WAF → General Protection. Account-level page (not per-website): mirrors the
 * vendor's WAF tab, composed from three private-portal reads anchored on any one
 * website in the service (the reads are account-wide, the domain only resolves
 * the customer session server-side):
 *   - WAF Monitoring Mode  ← waf_strategies rows with monitor_only = true
 *   - WAF Protection Policy ← managed catalog × waf_strategies/mapping
 *   - Custom WAF Protection Policy ← waf_custom_strategies
 *
 * Monitoring-mode toggling and managed-policy apply are wired (PUT /waf_strategy,
 * PUT /waf_strategies). Custom-policy add/edit/delete still await vendor
 * endpoints, so those controls remain disabled.
 */
export function GeneralProtectionTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;
  // General Protection is account-level: resolves the vendor customer from the
  // service, so it renders even before the account has any website.
  const { show } = useToast();

  const strategies = useServiceWafStrategies(serviceId);
  const mapping = useServiceWafStrategiesMapping(serviceId);
  const custom = useServiceWafCustomStrategies(serviceId);

  // Custom policy delete, confirmed before it fires.
  const [pendingDelete, setPendingDelete] = useState<{ id: number; label: string } | null>(null);
  const deleteCustom = useDeleteServiceCustomPolicy(serviceId ?? '', {
    onSuccess: () => {
      trackFeatureAction('waap', 'custom_waf_policy', 'delete');
      show({ tone: 'success', title: 'Custom WAF protection policy deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const strategyRows = useMemo(
    () => waapAsArray<WafStrategyRow>(strategies.data),
    [strategies.data],
  );
  const monitorRows = useMemo(
    () => strategyRows.filter((r) => r.monitor_only),
    [strategyRows],
  );

  // Protected sites with a resolvable portal site id (needed for the writes).
  const sites = useMemo<SiteInfo[]>(() => {
    const out: SiteInfo[] = [];
    for (const r of strategyRows) {
      const siteId = asId(r.site_id);
      const domain = typeof r.domain === 'string' ? r.domain.trim() : '';
      if (siteId == null || !domain) continue;
      out.push({ siteId, domain, monitorOnly: !!r.monitor_only, strategyType: asId(r.strategy_type) });
    }
    return out;
  }, [strategyRows]);

  // strategy_type → domains currently running that policy.
  const domainsByPolicy = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const row of waapAsArray<WafStrategyMappingRow>(mapping.data)) {
      const id = asId(row.strategyType);
      if (id != null) m.set(id, Array.isArray(row.domainList) ? row.domainList : []);
    }
    return m;
  }, [mapping.data]);

  const customRows = useMemo(
    () => waapAsArray<WafCustomStrategyRow>(custom.data),
    [custom.data],
  );

  const loading = strategies.isLoading || mapping.isLoading || custom.isLoading;

  // ---- Mutations --------------------------------------------------------
  const setMonitor = useSetServiceWafMonitor(serviceId ?? '');
  const applyPolicy = useApplyServiceWafPolicy(serviceId ?? '');

  const [monitorOpen, setMonitorOpen] = useState(false);
  const [applyFor, setApplyFor] = useState<{ id: number; label: string } | null>(null);
  const [detailFor, setDetailFor] = useState<WafPolicy | null>(null);
  const [pendingRemoveMonitor, setPendingRemoveMonitor] = useState<WafStrategyRow | null>(null);

  const removeFromMonitoring = async (siteId: number) => {
    try {
      await setMonitor.mutateAsync({ site_id: siteId, monitor_only: false });
      // track feature engagement
      trackFeatureAction('waap', 'waf_monitor_mode', 'disable');
      show({ tone: 'success', title: 'Removed from monitoring mode' });
    } catch (e) {
      show({ tone: 'danger', title: 'Update failed', description: errMessage(e) });
    }
  };

  const addToMonitoring = async (siteIds: number[]) => {
    try {
      await Promise.all(
        siteIds.map((id) => setMonitor.mutateAsync({ site_id: id, monitor_only: true })),
      );
      // track feature engagement
      trackFeatureAction('waap', 'waf_monitor_mode', 'enable');
      show({ tone: 'success', title: `Added ${siteIds.length} website(s) to monitoring mode` });
      setMonitorOpen(false);
    } catch (e) {
      show({ tone: 'danger', title: 'Update failed', description: errMessage(e) });
    }
  };

  const doApply = async (type: number, siteIds: number[]) => {
    try {
      await applyPolicy.mutateAsync({ type, site_ids: siteIds });
      // track feature engagement
      trackFeatureAction('waap', 'waf_protection_policy', 'edit');
      show({ tone: 'success', title: 'Protection policy applied' });
      setApplyFor(null);
    } catch (e) {
      show({ tone: 'danger', title: 'Apply failed', description: errMessage(e) });
    }
  };

  // ---- Columns ----------------------------------------------------------
  const monitorColumns = useMemo<ColumnDef<WafStrategyRow>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: 'Website',
        cell: ({ row }) => <span className={styles.name}>{row.original.domain}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const siteId = asId(row.original.site_id);
          return (
            <div className={styles.rowEnd}>
              <IconButton
                aria-label="Remove from monitoring mode"
                variant="ghost"
                disabled={siteId == null || setMonitor.isPending}
                onClick={() => siteId != null && setPendingRemoveMonitor(row.original)}
              >
                <Trash2 size={16} />
              </IconButton>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMonitor.isPending],
  );

  const policyColumns = useMemo<ColumnDef<(typeof WAF_POLICIES)[number]>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Protection Policy',
        cell: ({ row }) => <span className={styles.name}>{row.original.label}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span className={styles.policyDesc}>
            {row.original.description}
          </span>
        ),
      },
      {
        id: 'website',
        header: 'Website',
        cell: ({ row }) => <DomainList domains={domainsByPolicy.get(row.original.id) ?? []} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className={styles.rowActions}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setApplyFor({ id: row.original.id, label: row.original.label })}
            >
              Apply to…
            </Button>
            <IconButton
              aria-label="Policy detail"
              variant="ghost"
              title="View policy detail"
              onClick={() => setDetailFor(row.original)}
            >
              <Eye size={16} />
            </IconButton>
          </div>
        ),
      },
    ],
    [domainsByPolicy],
  );

  const customColumns = useMemo<ColumnDef<WafCustomStrategyRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Protection Policy',
        cell: ({ row }) => (
          <span className={styles.name}>{row.original.name || muted('—')}</span>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.comment || muted('—'),
      },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) => {
          const tpl = row.original.template_label;
          return tpl ? <StatusPill tone="info">{tpl}</StatusPill> : muted('—');
        },
      },
      {
        id: 'website',
        header: 'Website',
        // Applying a custom policy writes the same strategy→domain mapping the
        // managed policies use, keyed by the custom policy's own id — the policy
        // row itself carries no domains.
        cell: ({ row }) => {
          const id = asId(row.original.id);
          return <DomainList domains={(id != null && domainsByPolicy.get(id)) || []} />;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const id = asId(row.original.id);
          return (
            <div className={styles.rowActions}>
              <Button
                size="sm"
                variant="secondary"
                disabled={id == null}
                onClick={() => id != null && setApplyFor({ id, label: row.original.name || `#${id}` })}
              >
                Apply to…
              </Button>
              <IconButton
                aria-label="Edit"
                variant="ghost"
                disabled={id == null}
                title="Edit policy"
                onClick={() =>
                  id != null &&
                  navigate(`${base}/aiwaf/custom-policy/${id}`, { state: { policy: row.original } })
                }
              >
                <Pencil size={16} />
              </IconButton>
              <IconButton
                aria-label="Delete"
                variant="ghost"
                disabled={id == null}
                title="Delete policy"
                onClick={() =>
                  id != null && setPendingDelete({ id, label: row.original.name || `#${id}` })
                }
              >
                <Trash2 size={16} />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [base, navigate, domainsByPolicy],
  );

  // Request pipeline (mirrors the CDN WAF Overview): incoming → monitoring →
  // protection policy → custom policy → origin, annotated with live counts.
  const flowColumns: FlowNode[][] = [
    [{ id: 'in', title: 'Incoming', to: ['monitor'] }],
    [{ id: 'monitor', title: 'Monitoring mode', dotColor: monitorRows.length ? toneColor('info') : undefined, to: ['policy'], children: <FlowRow label="Sites" value={String(monitorRows.length)} /> }],
    [{ id: 'policy', title: 'Protection policy', dotColor: toneColor('success'), to: ['custom'], children: <FlowRow label="Managed" value={`${WAF_POLICIES.length} policies`} /> }],
    [{ id: 'custom', title: 'Custom policy', to: ['origin'], children: <FlowRow label="Rules" value={String(customRows.length)} /> }],
    [{ id: 'origin', title: 'Origin' }],
  ];

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'General Protection' }]} />
        }
        title="General Protection"
        description="Machine learning and semantic analysis empower threat detection, strengthening rule-based WAF to protect websites from malicious threats."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {strategies.isError && (
            <Alert tone="danger" title="Couldn't load WAF strategies">
              The WAF protection data couldn't be fetched. Please retry shortly.
            </Alert>
          )}

          {/* Protection pipeline overview — mirrors the CDN WAF Overview tab. */}
          <Card>
            <CardHeader>
              <CardTitle>Protection pipeline</CardTitle>
              <CardDescription>
                How a request flows through WAAP&apos;s WAF tiers before reaching your origin.
              </CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.flowScroll}>
                <FlowDiagram columns={flowColumns} />
              </div>
            </CardBody>
          </Card>

          {/* 1 — WAF Monitoring Mode */}
          <Section
            style={{ marginBottom: 0 }}
            title="WAF Monitoring Mode"
            description="When a website runs under monitoring mode the WAF takes no action on abnormal requests but still records them in Analytics."
            actions={
              <StatusPill tone={monitorRows.length ? 'info' : 'neutral'}>
                {monitorRows.length} monitoring
              </StatusPill>
            }
          >
            <div className={styles.toolbar}>
              <Button
                size="sm"
                variant="secondary"
                startIcon={<Plus size={14} />}
                disabled={sites.every((s) => s.monitorOnly)}
                onClick={() => setMonitorOpen(true)}
              >
                Add website to monitoring
              </Button>
            </div>
            {loading ? (
              <Skeleton style={{ height: 80 }} />
            ) : (
              <DataTable
                columns={monitorColumns}
                data={monitorRows}
                emptyState={
                  <EmptyState
                    size="sm"
                    icon={<ShieldCheck size={20} />}
                    title="No monitoring websites"
                    description="No website is in monitoring mode yet. Add one to record abnormal requests without blocking them."
                  />
                }
              />
            )}
          </Section>

          {/* 2 — WAF Protection Policy (managed catalog) */}
          <Section
            style={{ marginBottom: 0 }}
            title="WAF Protection Policy"
            description="Conversant's managed WAF protection policies are designed to cover all aspects of business security requirements."
          >
            {loading ? (
              <Skeleton style={{ height: 200 }} />
            ) : (
              <div className={styles.tableScroll}>
                <DataTable columns={policyColumns} data={WAF_POLICIES} />
              </div>
            )}
          </Section>

          {/* 3 — Custom WAF Protection Policy (temporarily hidden) */}
          {SHOW_CUSTOM_WAF_POLICY && (
          <Section
            style={{ marginBottom: 0 }}
            title="Custom WAF Protection Policy"
            description="Customise the WAF protection policy based on the provided policy templates."
          >
            <div className={styles.toolbar}>
              <Button
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={() => navigate(`${base}/aiwaf/custom-policy/new`)}
              >
                Add Custom WAF Protection Policy
              </Button>
            </div>
            {loading ? (
              <Skeleton style={{ height: 100 }} />
            ) : (
              <DataTable
                columns={customColumns}
                data={customRows}
                emptyState={
                  <EmptyState size="sm" icon={<ShieldCheck size={20} />} title="No custom policies yet" description="Add a custom WAF protection policy to extend the managed ruleset." />
                }
              />
            )}
          </Section>
          )}
        </div>

      {monitorOpen && (
        <MonitorAddDialog
          sites={sites}
          pending={setMonitor.isPending}
          onClose={() => setMonitorOpen(false)}
          onAdd={addToMonitoring}
        />
      )}
      {applyFor && (
        <ApplyPolicyDialog
          key={applyFor.id}
          policy={applyFor}
          sites={sites}
          pending={applyPolicy.isPending}
          onClose={() => setApplyFor(null)}
          onApply={(siteIds) => doApply(applyFor.id, siteIds)}
        />
      )}
      {detailFor && (
        <PolicyDetailDialog policy={detailFor} onClose={() => setDetailFor(null)} />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this custom policy?"
        description={`"${pendingDelete?.label ?? ''}" will be removed. Websites running it fall back to their managed protection policy.`}
        confirmLabel="Delete"
        loading={deleteCustom.isPending}
        onConfirm={() => pendingDelete && deleteCustom.mutate(pendingDelete.id)}
      />

      <ConfirmDialog
        open={!!pendingRemoveMonitor}
        onOpenChange={(o) => !o && setPendingRemoveMonitor(null)}
        title="Remove from monitoring mode?"
        description={`"${String(pendingRemoveMonitor?.domain ?? '')}" will switch from monitoring-only back to active protection. The WAF will block abnormal requests again.`}
        confirmLabel="Remove"
        loading={setMonitor.isPending}
        onConfirm={() => {
          const siteId = pendingRemoveMonitor ? asId(pendingRemoveMonitor.site_id) : undefined;
          if (siteId != null) {
            void removeFromMonitoring(siteId);
            setPendingRemoveMonitor(null);
          }
        }}
      />
    </Container>
  );
}

/** Read-only detail for a managed WAF protection policy: Name / Description /
 *  Rule Group. All data is the fixed vendor catalog (no API). */
function PolicyDetailDialog({ policy, onClose }: { policy: WafPolicy; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{policy.label} Detail</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className={styles.detailGrid}>
            <div className={styles.detailLabel}>Name</div>
            <div>{policy.label}</div>

            <div className={styles.detailLabel}>Description</div>
            <div>{policy.description}</div>

            <div className={styles.detailLabel}>Rule Group</div>
            <ul className={styles.ruleGroupList}>
              {policy.ruleGroups.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog to add one or more not-yet-monitored websites to Monitoring Mode. */
function MonitorAddDialog({
  sites,
  pending,
  onClose,
  onAdd,
}: {
  sites: SiteInfo[];
  pending: boolean;
  onClose: () => void;
  onAdd: (siteIds: number[]) => void;
}) {
  const candidates = useMemo(() => sites.filter((s) => !s.monitorOnly), [sites]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add websites to monitoring mode</DialogTitle>
          <DialogDescription>
            Selected websites switch to monitor-only: the WAF records abnormal requests without
            blocking them.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {candidates.length === 0 ? (
            <EmptyState icon={<ShieldCheck size={20} />} title="All websites monitored" description="All protected websites are already in monitoring mode." />
          ) : (
            <div className={styles.checkList}>
              {candidates.map((s) => (
                <label key={s.siteId} className={styles.checkRow}>
                  <Checkbox checked={selected.has(s.siteId)} onCheckedChange={() => toggle(s.siteId)} />
                  <span>{s.domain}</span>
                </label>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending || selected.size === 0} onClick={() => onAdd([...selected])}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog to apply a managed protection policy to a set of sites. Sites already
 *  on this policy are checked and locked (the vendor keeps them on it). */
function ApplyPolicyDialog({
  policy,
  sites,
  pending,
  onClose,
  onApply,
}: {
  policy: { id: number; label: string };
  sites: SiteInfo[];
  pending: boolean;
  onClose: () => void;
  onApply: (siteIds: number[]) => void;
}) {
  const current = useMemo(
    () => new Set(sites.filter((s) => s.strategyType === policy.id).map((s) => s.siteId)),
    [sites, policy.id],
  );
  const [selected, setSelected] = useState<Set<number>>(() => new Set(current));
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply {policy.label} Protection Policy to</DialogTitle>
          <DialogDescription>
            Choose the websites to protect with the {policy.label} policy. Websites already on this
            policy stay selected.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {sites.length === 0 ? (
            <EmptyState icon={<Globe size={20} />} title="No websites available" description="Add a website to this service before applying protection policies." />
          ) : (
            <div className={styles.checkList}>
              {sites.map((s) => {
                const locked = current.has(s.siteId);
                return (
                  <label
                    key={s.siteId}
                    style={{
                      display: 'flex',
                      gap: 'var(--vn-space-2)',
                      alignItems: 'center',
                      cursor: locked ? 'default' : 'pointer',
                      color: locked ? 'var(--vn-fg-muted)' : undefined,
                    }}
                  >
                    <Checkbox
                      checked={selected.has(s.siteId)}
                      disabled={locked}
                      onCheckedChange={() => toggle(s.siteId)}
                    />
                    <span>{s.domain}</span>
                  </label>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending || selected.size === 0} onClick={() => onApply([...selected])}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
